import ChampionshipArtworkCell from '../ChampionshipArtworkCell.jsx'
import {useArtworkCreated} from '../../artwork-created/useArtworkCreated.js'
import {ArtworkProgress} from '../../artwork-created/ArtworkProgress.jsx'
import {contactRecordBinding,needsSchoolCandidates,hasSchoolReviewIntent} from '../../../lib/currentChampionshipContacts.js'
import SchoolTeamCoverage from '../../bowl/SchoolTeamCoverage.jsx'
import SchoolCoachCandidates from './SchoolCoachCandidates.jsx'
import ChampionshipContact from './ChampionshipContact.jsx'
import useVisibleChampionshipContacts from './useVisibleChampionshipContacts.js'
import { recordedChampionFacts } from '../../../lib/recordedChampionFacts.js'
import { titleTierLabel, titleTierFor } from '../../../lib/titleTierView.js'
import { daysToEvent, formatDate, formatCountdownFor, pursuitMeta, sportMeta, ownerName } from '../../../lib/championships.js'
import { worklistNextWork } from './worklistNextWork.js'
import { Th } from './SortControls.jsx'
import { SelectAllTh, SelectCell } from './RowSelect.jsx'
import styles from './pursuit.module.css'

// Isolated row component keeps hooks out of the map and uses the same
// permission-scoped projection as the original drawer and Artwork Created tool.
function RecordNextWork({record,work,onOpen}) {
  const read = useArtworkCreated(record?.sourceType || record?.isIndyGroup ? null : record?.id)
  return <RecordNextWorkView read={read} record={record} work={work} onOpen={onOpen} />
}
export function RecordNextWorkView({read,record,work,onOpen}) {
  const rows=read.rows?.length?read.rows:read.row?[read.row]:[]
  if (rows.length) return <>{rows.map(row=><ArtworkProgress key={row.jobId} row={row} compact />)}
    {onOpen && <button type="button" className={styles.workOpen} onClick={event => {event.stopPropagation();onOpen(record)}}>Review saved artwork &amp; next action</button>}
  </>
  return <>
    {['error','conflict'].includes(read.state) && <p role="status">Artwork status unavailable; saved artwork may exist. Do not recreate it to refresh the read.</p>}
    {work ? <><p>{work.next}</p>
      {onOpen && <button type="button" className={styles.workOpen} onClick={event => {event.stopPropagation();onOpen(record,work.kind)}}>{work.action}</button>}
    </> : 'Open record to review next steps'}
  </>
}

/**
 * THE RECORD TABLE — one row markup, every caller.
 *
 * This was the Worklist's inlined <table>. It is now shared with the Countdown
 * Calendar's day panel, because "clicking a date shows every championship on
 * that date" should show the SAME rows the Worklist shows, opening the SAME
 * drawer — not a second, subtly different list of the same records.
 *
 * That is not a style preference in this repo. Two normalizers deciding
 * folder-vs-design separately is what broke Canva drill-in on the live site
 * (v1.36.1); two copies of a row are the same shape of bug waiting to happen,
 * and the row is where the champion, the owner and the date provenance are
 * read. One component, both callers.
 *
 * Presentation hooks are preserved verbatim: `cc-cards-wrap` / `cc-cards` and
 * the per-cell `data-col` / `data-label` attributes keep mobile labels stable.
 * The scoped cc-work-table layer uses cards at narrower desktop widths too,
 * so owner, contact and actions do not disappear in a horizontal scroller.
 */
function renderRecordTable({
  rows, now, onOpen, sort, setSort, selection = null,
  noun = 'Championship', dateLabel = 'Event date', hasProduct = false,
  selectAllLabel = 'rows', contactsEnabled = false, contacts = {root:null,values:{},scope:null},
  /* SELECT-ALL GOVERNS THE FULL LIST, NOT THE RENDERED PAGE.
   *
   * `rows` is the page; `selectAllRows` is everything behind it. Extracting
   * this table nearly lost that distinction — the Worklist's select-all ticked
   * its whole bucket and would silently have become "tick the first 200". A
   * select-all that quietly means something narrower than "all" is exactly the
   * kind of arithmetic this board keeps getting bitten by, so the two lists are
   * separate arguments rather than one shared variable. */
  selectAllRows = null,
}) {
  const governs = selectAllRows || rows
  const hasChampionships = rows.some(c => !c.sourceType)
  const columns = [selection && 3, 15, 6, hasProduct && 7, 7, 6, contactsEnabled && hasChampionships && 16, hasChampionships && 17, hasChampionships && 14, hasChampionships && 12, 6, 9, 3].filter(Boolean)
  const widthTotal = columns.reduce((sum, width) => sum + width, 0)
  return (
    <div ref={contacts.root} data-record-table className={`${styles.tableWrap} cc-cards-wrap cc-scrollcue`}>
      {selection && <div className={styles.more} data-worklist-selection>
        <button type="button" className={styles.moreBtn} onClick={() => selection.setMany(governs, selection.countIn(governs) !== governs.length)}>{selection.countIn(governs) === governs.length ? 'Deselect' : 'Select'} all {governs.length.toLocaleString()} {selectAllLabel}</button>
        <span>{selection.countIn(governs).toLocaleString()} selected{governs.length > rows.length ? ', including rows not displayed yet' : ''}.</span>
      </div>}
      <table className={`${styles.table} cc-cards cc-work-table`}>
        <colgroup>{columns.map((width, index) => <col key={index} style={{width: `${width / widthTotal * 100}%`}} />)}</colgroup>
        <thead>
          <tr>
            {/* Select-all governs the FULL list handed in, not the rendered page. */}
            <SelectAllTh rows={governs} selection={selection} label={selectAllLabel} />
            <Th field="name" label={noun} sort={sort} setSort={setSort} />
            <Th field="sport" label="Sport / level" sort={sort} setSort={setSort} />
            {hasProduct && <Th field="product" label="Product" sort={sort} setSort={setSort} />}
            <Th field="date" label={dateLabel} sort={sort} setSort={setSort} />
            <th>Countdown</th>
            {contactsEnabled && hasChampionships && <th>Contact</th>}
            {hasChampionships && <><th>Champion / evidence</th><th>Artwork</th><th>Next work</th></>}
            <Th field="pursuit" label="Status" sort={sort} setSort={setSort} />
            <Th field="owner" label="Assigned rep" sort={sort} setSort={setSort} />
            <Th field="state" label="State" sort={sort} setSort={setSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const days = daysToEvent(c, now)
            const pm = pursuitMeta(c.pursuit)
            const sm = sportMeta(c.sport)
            const owner = ownerName(c)
            const work = !c.sourceType ? worklistNextWork(c, now) : null
            const facts = recordedChampionFacts(c)
            /* ⚠️ `aria-disabled` WHEN THERE IS NO `onOpen`. Every handler on
             * this row is `onOpen ? fn : undefined`, so without the prop the row
             * is dead while still reading as a record you can open — the shape
             * check-clickable's CONDITIONAL rule now catches, and the same shape
             * as the dossier button and "Request an exec push". The class
             * already switches; the row has to tell a screen reader too, not
             * only a stylesheet. */
            return (
              <tr
                key={c.id}
                className={onOpen ? styles.rowClickable : ''}
                aria-disabled={onOpen ? undefined : 'true'}
                onClick={onOpen ? () => onOpen(c) : undefined}
              >
                <SelectCell item={c} selection={selection} />
                <td className={styles.tName} data-col="name"><span className={styles.sportAccent} style={{ background: sm.color }} title={sm.label} />{onOpen ? <button type="button" className={styles.recordNameButton} onClick={event => { event.stopPropagation(); onOpen(c) }}>{c.name}</button> : c.name}{c.titleTier && <span className={styles.badge} title={titleTierFor(c).titleTierReason}>{titleTierLabel(c)}</span>}{onOpen && <span className={styles.openHint}> ›</span>}</td>
                <td className={styles.tDim} data-col="sport" data-label="Sport / level"><span className={styles.sportDot} style={{ background: sm.color }} />{[c.sport, c.level].filter(Boolean).join(' · ') || '—'}</td>
                {hasProduct && <td className={c.product ? styles.tDim : styles.tGap} data-col="product" data-label="Product">{c.product || '—'}</td>}
                <td className={c.effDate ? '' : styles.tGap} data-col="date" data-label={dateLabel}>{c.effDate ? formatDate(c.effDate) : 'NO DATE'}{c.dateEstimated && <span className={styles.estBadge} title="Estimated date">est</span>}</td>
                <td className={styles.tNum} data-col="countdown" data-label="Countdown">{formatCountdownFor(c, days)}</td>
                {contactsEnabled && hasChampionships && <td data-col="contact" data-label="Contact" data-contact-id={!c.sourceType && /^\d+$/.test(String(c.id)) ? c.id : undefined} style={{minWidth:0,maxWidth:300,whiteSpace:'normal',overflowWrap:'anywhere'}}>
                  {!c.sourceType && /^\d+$/.test(String(c.id)) ? <><ChampionshipContact key={contacts.scope+':'+c.id} value={contacts.values[String(c.id)]} onRead={()=>contacts.read(c.id)} />{contacts.values[String(c.id)]?.state !== 'restricted' && <SchoolCoachCandidates key={contacts.viewScope+':candidates:'+contactRecordBinding(c)} recordId={c.id} binding={contactRecordBinding(c)} viewScope={contacts.viewScope} autoFallback={contacts.autoFallbackEnabled && (needsSchoolCandidates(contacts.values[String(c.id)]) || hasSchoolReviewIntent(c.id))}/>}</> : c.sourceType ? 'Not a championship' : 'Open an actual championship member to read linked contacts'}
                </td>}
                {hasChampionships && <>
                  <td className={styles.workEvidence} data-col="champion" data-label="Champion / evidence">
                    {work ? <><span data-recorded-champion><strong>{work.champion}</strong>
                      {facts.claims.filter(claim => !(work.displayedClaims || []).some(displayed => displayed.trim().toLowerCase() === claim.trim().toLowerCase())).map(claim => <span className={styles.workMember} key={claim}>Additional recorded claim: {claim}</span>)}
                      {facts.members.map((member, index) => <span className={styles.workMember} key={index}>Member/reference: {member}</span>)}
                      {facts.referencesError && <span>{facts.referencesError}</span>}
                    </span><p className={styles.tDim}>{work.kind === 'multi' ? facts.note : work.evidence}</p>
                      {work.source && <a className={styles.workOpen} href={work.source} target="_blank" rel="noreferrer" onClick={event => event.stopPropagation()}>Review source</a>}
                    </> : 'Not a championship'}
                  </td>
                  <td className={styles.tArtwork} data-col="artwork" data-label="Artwork" onClick={event=>event.stopPropagation()}>{!c.sourceType?<ChampionshipArtworkCell record={c}/>:'Not a championship'}</td>
                  <td className={styles.workNext} data-col="next-work" data-label="Next work">
                    <RecordNextWork record={c} work={work} onOpen={onOpen} />
                  </td>
                </>}
                <td data-col="status" data-label="Status"><span className={styles.statusChip} style={{ color: pm.color, borderColor: pm.color + '55' }}>{c.sourceType && c.stageLabel ? c.stageLabel : pm.label}</span></td>
                <td className={owner ? styles.tDim : styles.tGap} data-col="owner" data-label="Assigned rep">{owner || 'No stored CRM rep'}<SchoolTeamCoverage record={c}/></td>
                <td className={c.state !== 'NATIONAL' ? styles.tDim : styles.tGap} data-col="state" data-label="State">{c.state !== 'NATIONAL' ? c.state : '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ContactRecordTable(props) {
 const contacts=useVisibleChampionshipContacts(props.rows,true)
 return renderRecordTable({...props,contacts})
}
export default function RecordTable(props) {
 return props.contactsEnabled ? <ContactRecordTable {...props} /> : renderRecordTable(props)
}
